<script lang="ts">
  import { authState } from "../auth/store.svelte";
  import type { GmailMessage } from "../gmail/store.svelte";
  import { getHeader, parseFrom } from "../gmail/api";
  import { extractBody, extractAttachments, plainToHtml } from "../gmail/parser";
  import { sanitizeEmailHtml } from "../gmail/sanitize";
  import { buildEmailSrcdoc } from "../gmail/srcdoc";
  import MessageAttachments from "./MessageAttachments.svelte";
  import Icon from "./shared/Icon.svelte";

  interface Props {
    message: GmailMessage;
    /** True = dernier message du thread → outline accent (et ouvert par défaut) */
    isLatest?: boolean;
    /** Prénom de l'autre party — pour "Me to Carlos / Carlos to Me" */
    counterpartyFirstName?: string;
  }

  let { message, isLatest = false, counterpartyFirstName = "" }: Props = $props();

  // Le dernier message du thread est expanded au mount, les autres collapsed.
  // svelte-ignore state_referenced_locally
  let expanded = $state(isLatest);
  let messageEl: HTMLDivElement | undefined = $state();
  let frameEl: HTMLIFrameElement | undefined = $state();
  let frameHeight = $state(120); // fallback initial avant la 1ère mesure

  // === Headers ============================================================
  const fromRaw = $derived(getHeader(message, "From"));
  const from = $derived(parseFrom(fromRaw));
  const date = $derived(parseInt(message.internalDate, 10));

  const fromMe = $derived(
    !!authState.email &&
    from.email.toLowerCase() === authState.email.toLowerCase(),
  );

  // === Body ==============================================================
  const body = $derived(extractBody(message));
  const rawHtml = $derived(
    body.html?.trim() ? body.html : body.text ? plainToHtml(body.text) : "",
  );
  // Pièces jointes (filtre les inline cid: → pas inclus ici)
  const attachments = $derived(extractAttachments(message));
  const sanitized = $derived(expanded ? sanitizeEmailHtml(rawHtml, true) : "");

  const snippetText = $derived(
    message.snippet?.trim() ||
    body.text?.slice(0, 240) ||
    "(no preview)",
  );

  // === Iframe srcdoc ======================================================
  // Pourquoi un iframe : le viewport interne = largeur de l'iframe (pas du
  // parent). Les @media queries du mail (typiques pour les emails marketing
  // responsive) voient une largeur "mobile" et appliquent leur layout mobile
  // naturellement. Sans iframe on aurait toujours le layout desktop juste shrunk.
  //
  // Sécurité (sandbox attribute) :
  //   - pas de allow-same-origin → opaque origin, pas d'accès cookies parent
  //   - allow-popups : liens s'ouvrent en nouvelle fenêtre
  //   - allow-popups-to-escape-sandbox : nouvelle fenêtre PAS sandboxée
  //   - allow-scripts : nécessaire pour le height-reporting (sanitization a
  //     déjà strip les <script> du mail)
  //
  // La génération du srcdoc est dans un .ts séparé (srcdoc.ts) pour éviter
  // que le parser Svelte interprète les <script>/<html>/etc inline.
  const srcdoc = $derived(expanded ? buildEmailSrcdoc(sanitized, message.id) : "");

  // === Listener postMessage pour height ===================================
  $effect(() => {
    if (!expanded) return;
    function onMessage(e: MessageEvent) {
      if (!frameEl || e.source !== frameEl.contentWindow) return;
      const data = e.data as { type?: string; id?: string; h?: number };
      if (data?.type === "mfx-h" && data.id === message.id && typeof data.h === "number") {
        frameHeight = Math.max(60, Math.min(data.h, 20000)); // safety cap 20k px
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  });

  // === Auto-scroll au tap (pas au mount initial) ==========================
  let firstExpandFired = false;
  $effect(() => {
    if (!expanded) return;
    if (!firstExpandFired && isLatest) {
      firstExpandFired = true;
      return;
    }
    firstExpandFired = true;
    if (!messageEl) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messageEl?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });
  });

  // === Ligne de méta « Carlos → Moi · mar. 16:20 » ========================
  function relativeLabel(unixMs: number): string {
    const d = new Date(unixMs);
    const now = new Date();
    const sameDay =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();
    if (sameDay) {
      return `aujourd'hui ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
    }
    const sameYear = d.getFullYear() === now.getFullYear();
    const opts: Intl.DateTimeFormatOptions = sameYear
      ? { weekday: "short", hour: "2-digit", minute: "2-digit" }
      : { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" };
    return d.toLocaleString("fr-FR", opts);
  }

  function senderShortName(): string {
    const name = from.name?.trim();
    if (name) return name.split(/\s+/)[0];
    return from.email.split("@")[0] || "unknown";
  }

  const senderLabel = $derived(
    fromMe
      ? counterpartyFirstName
        ? `Moi → ${counterpartyFirstName}`
        : "Moi"
      : `${senderShortName()} → Moi`,
  );
</script>

<div
  class="message"
  class:from-me={fromMe}
  class:from-them={!fromMe}
  class:is-expanded={expanded}
  bind:this={messageEl}
>
  <div class="label">
    <span class="who">{senderLabel}</span>
    <span class="when">{relativeLabel(date)}</span>
  </div>

  {#if !expanded}
    <button class="collapsed" onclick={() => (expanded = true)} aria-label="Déplier le message">
      {snippetText}
    </button>
  {:else}
    <div class="card" class:from-me={fromMe}>
      <button
        class="collapse-btn"
        onclick={() => (expanded = false)}
        aria-label="Réduire"
      >
        <Icon name="close" size={14} />
      </button>

      <MessageAttachments {attachments} messageId={message.id} />

      <iframe
        bind:this={frameEl}
        srcdoc={srcdoc}
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts"
        title="Email content"
        style:height="{frameHeight}px"
      ></iframe>
    </div>
  {/if}
</div>

<style>
  /* Plus de bulles de messagerie : des cartes, comme le desktop. Le seul
     marqueur d'auteur est le liseré gauche — l'alignement gauche/droite
     coûtait la moitié de la largeur de lecture sur un écran de 375px. */
  .message {
    display: flex;
    flex-direction: column;
    margin-bottom: 14px;
  }

  .label {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 0 3px 5px;
  }
  .who {
    font-size: 11.5px;
    font-weight: 550;
    color: var(--fg-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .when {
    flex-shrink: 0;
    font-family: var(--font-mono);
    font-size: 9.5px;
    font-variant-numeric: tabular-nums;
    color: var(--fg-subtle);
  }

  /* Replié : le même cadre, sans liseré, sur une seule ligne. */
  .collapsed {
    display: block;
    width: 100%;
    padding: 10px 13px;
    background: var(--bg);
    border: 1px solid var(--border-subtle);
    border-radius: 11px;
    box-shadow: var(--shadow-card);
    font-size: 12.5px;
    color: var(--fg-subtle);
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .collapsed:active { background: var(--bg-hover); }

  .card {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: var(--bg);
    border: 1px solid var(--border-subtle);
    /* Reçu = accent, envoyé = neutre. Un seul marqueur, à gauche. */
    border-left: 3px solid var(--accent);
    border-radius: 11px;
    box-shadow: var(--shadow-card);
  }
  .card.from-me { border-left-color: var(--border); }

  iframe {
    width: 100%;
    border: none;
    display: block;
    background: var(--bg);
  }

  .collapse-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 26px;
    height: 26px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: var(--bg-hover);
    color: var(--fg-muted);
    z-index: 2;
  }
</style>
